import { WithExportButton, WithExportModal, WithHiddenListBox } from '@components/design';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
  BrandDependencyKpiOption,
  BrandDependencySortOption,
  kpiToOption,
  sortByToOption,
} from '@data-visualization/brand-dependency-chart/BrandDependencyChartUtils';
import { BrandDependencyKpi, BrandDependencySort, buildOptions, ComponentName } from '@models/BrandDependency';
import {
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
  useGenerateChartInteractionTrackingParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import { FeatureOption, FilterKeys, Filters, FilterTitle, Option, PageName, PageType } from '@utils/pageConfig/Types';
import {
  pageElementViewTrackingFunction,
  useGeneratePageElementViewParam,
} from '@utils/page-analytics/model/PageElementViewedTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { elements, feedback } from '@ecosystem/newron-design-system';
import { useExportModal } from '@hooks/export/export-modal/UseExportModal';
import { EnabledExportPages, ViewType } from '@hooks/export/Model';
import { CreateViewStateFn, ExportFormat } from '@ecosystem/newron-client-lib';
import { TradingAssistantTabView } from '@components/tab-nav/TabConfig';
import TACollectionButton from '../collection-button/TACollectionButton';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { TradingAssistantButterflyChartInsight } from '@components/collections/trading-assistant-butterfly-chart/Model';
import {
  dataExportInitiatedTrackingFunction,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import { SortDirection, SortingOptions } from '@components/design/migrated/Table/Sorting/types';

interface Props {
  selectedKpi: BrandDependencyKpi;
  setSelectedKpi: Dispatch<SetStateAction<BrandDependencyKpi>>;
  selectedSortBy: BrandDependencySort;
  setSelectedSortBy: Dispatch<SetStateAction<BrandDependencySort>>;
  features: Filters[];
  bySegment: FeatureOption;
  setFeatureOptions: Dispatch<SetStateAction<FeatureOption>>;
  isLoading: boolean;
  onChartDownload: (exportFormats: ExportFormat[]) => void;
  createInsightDataFn?: CreateInsightDataFn<TradingAssistantButterflyChartInsight>;
  selectedBrands?: Option[];
  createViewStateFn: CreateViewStateFn<any>;
}

const TooltipContent = () => (
  <feedback.Tooltip testId="brand-dependency-card-tooltip" placement="top" theme="light">
    <feedback.Tooltip.Content>
      <>
        <p>
          Understand how important you are to brands turnover - See the importance of brands in the market and their
          relative importance in your sales overall.
        </p>
        <p>
          Understand positions of strength and dependency to support your supplier negotiation discussions and inform
          range selection decisions.
        </p>
      </>
    </feedback.Tooltip.Content>
    <div>
      <elements.Icon color="brand">
        <elements.Icon.icons.HelpOutline />
      </elements.Icon>
    </div>
  </feedback.Tooltip>
);

const BrandDependencyFilter: React.FC<Props> = ({
  selectedKpi,
  setSelectedKpi,
  selectedSortBy,
  setSelectedSortBy,
  features,
  bySegment,
  setFeatureOptions,
  isLoading,
  onChartDownload,
  createInsightDataFn,
  selectedBrands,
  createViewStateFn,
}) => {
  const [featureValue, setFeatureValue] = useState<string | null>(bySegment.value);
  const chartInteractionParam = useGenerateChartInteractionTrackingParam('marketPremiumRetailerClient');
  const pageElementViewParam = useGeneratePageElementViewParam('mainFiltersMarketPremium');
  const pagesConfig = usePageConfig();
  const allKpis: BrandDependencyKpiOption[] = Object.keys(BrandDependencyKpi).map((kpi) =>
    kpiToOption(kpi as BrandDependencyKpi),
  );

  const allSortBys: BrandDependencySortOption[] = Object.keys(BrandDependencySort).map((sortBy) =>
    sortByToOption(sortBy as BrandDependencySort),
  );

  const bySegmentOptions: FeatureOption[] = (() => {
    const defaultOption: FeatureOption = {
      dataType: FilterTitle.SELECT,
      label: FilterTitle.SELECT,
      feature: FilterTitle.SELECT,
      value: FilterTitle.SELECT,
      filterKey: FilterKeys.SELECT,
    };
    const options = buildOptions(features);
    return [defaultOption, ...options];
  })();

  const onChangeTracking = (label: string, elementName: ChartElementName) => {
    const trackingParam: ChartInteractionTrackingFunctionParam = {
      ...chartInteractionParam,
      pageType: PageType.OPPORTUNITY_REVIEW,
      pageName: PageName.TRADING_ASSISTANT,
      elementName,
      elementType: ChartElementType.FILTER,
      optionChosen: label,
      chartName: ComponentName.RETAILER_MARKET_RELATIONSHIP_BUTTERFLY_CHART,
      access: pagesConfig.access,
    };
    chartInteractionTrackingFunction(trackingParam);
  };

  const customPageElementViewParam = () => ({
    ...pageElementViewParam,
    pageType: PageType.OPPORTUNITY_REVIEW,
    pageName: PageName.TRADING_ASSISTANT,
    componentName: ComponentName.RETAILER_MARKET_RELATIONSHIP_BUTTERFLY_CHART,
    elementType: ElementType.BUTTERFLY_CHART,
    access: pagesConfig.access,
  });

  const chartPageElementViewedTracking = () => {
    pageElementViewTrackingFunction(customPageElementViewParam());
  };

  const handleOptionChange = (
    option: string | null,
    setOption: Dispatch<SetStateAction<any>>,
    options: { value: string; label: string }[],
    elementName: ChartElementName,
  ) => {
    const selectedOption = options.find((o) => o.value === option);
    if (selectedOption) {
      setOption(selectedOption.value);
      onChangeTracking(selectedOption.label, elementName);
      chartPageElementViewedTracking();
    }
  };

  useEffect(() => {
    const selectedOption = bySegmentOptions.find((o) => o.value === featureValue);
    if (selectedOption) {
      setFeatureOptions(selectedOption);
    }
  }, [featureValue]);

  const getOptionLabel = (options: any[], value: string) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : null;
  };

  const memoizedSelectedShowLabelOption = useMemo(() => getOptionLabel(allKpis, selectedKpi), [selectedKpi]);

  const memoizedSelectedBySegmentLabelOption = useMemo(
    () => getOptionLabel(bySegmentOptions, featureValue ?? bySegmentOptions[0].value),
    [featureValue],
  );

  const memoizedSelectedSortByLabelOption = useMemo(() => getOptionLabel(allSortBys, selectedSortBy), [selectedSortBy]);

  const onExportClick = () => {
    handleExportTracking();
    openExportModal();
  };

  const handleDataExportTrackingFunction = useDataExportTracking('mainFiltersMarketPremium');

  const handleExportTracking = () => {
    const brandDependencySortOption = sortByToOption(BrandDependencySort[selectedSortBy]);
    const elementSort: SortingOptions = {
      headerKey: brandDependencySortOption.label,
      sortKey: brandDependencySortOption.value,
      sortDir: SortDirection.desc,
      label: brandDependencySortOption.label,
    };
    handleDataExportTrackingFunction(
      {
        elementType: ElementType.BUTTERFLY_CHART,
        elementKpi: selectedKpi,
        elementSort,
        brandApplied: selectedBrands?.map((option) => option.value).join(', ') ?? 'All',
      },
      dataExportInitiatedTrackingFunction,
    );
  };

  const {
    openExportModal,
    onCloseExportModal,
    onExportError,
    exportModalFormats,
    exportModalMessage,
    exportModalHeaderText,
    isExportModalOpen,
  } = useExportModal({
    currentPage: EnabledExportPages.TradingAssistant,
    currentTab: TradingAssistantTabView.SupplierReview,
    currentView: ViewType.ButterflyChart,
  });

  return (
    <div className="flex w-full items-center">
      <div className="flex items-center">
        <div>
          <WithHiddenListBox
            isDisabled={isLoading}
            label={'Show'}
            options={allKpis}
            value={memoizedSelectedShowLabelOption}
            onChange={({ value }) => handleOptionChange(value, setSelectedKpi, allKpis, ChartElementName.SHOW)}
          />
        </div>
        <div
          key={`${bySegmentOptions}-${featureValue}-${bySegment}`} //NOSONAR typescript:S6551
        >
          <WithHiddenListBox
            isDisabled={isLoading}
            label={'by'}
            options={bySegmentOptions}
            value={memoizedSelectedBySegmentLabelOption}
            onChange={({ value }) =>
              handleOptionChange(value, setFeatureValue, bySegmentOptions, ChartElementName.BY_SEGMENT)
            }
          />
        </div>
        <div>
          <WithHiddenListBox
            isDisabled={isLoading}
            label={'sorted by'}
            options={allSortBys}
            value={memoizedSelectedSortByLabelOption}
            onChange={({ value }) => handleOptionChange(value, setSelectedSortBy, allSortBys, ChartElementName.SORT_BY)}
          />
        </div>
        <div className="flex pl-4 pt-1">
          <TooltipContent />
        </div>
      </div>
      <div className="flex ml-auto">
        <TACollectionButton
          isDisabled={isLoading}
          createInsightDataFn={createInsightDataFn}
          createViewStateFn={createViewStateFn}
        ></TACollectionButton>
        <WithExportButton disabled={isLoading} onClick={onExportClick}></WithExportButton>
        <WithExportModal
          isShown={isExportModalOpen}
          exportFormats={exportModalFormats}
          message={exportModalMessage}
          title={exportModalHeaderText}
          download={onChartDownload}
          onClose={onCloseExportModal}
          onError={onExportError}
        />
      </div>
    </div>
  );
};

export default BrandDependencyFilter;
